const STORAGE_KEY = 'auth'

export interface AuthToken {
  access_token: string
  token_type: string
}

export interface JwtClaims {
  user_id: string
  scopes: string[]
  is_anonymous: boolean
  exp: number
}

// This module is imported by httpClient, which every provider (including
// dataProvider) goes through — some of those run their tests in a plain node
// environment with no `localStorage` global, so every access must be guarded.
function storage(): Storage | undefined {
  return typeof localStorage === 'undefined' ? undefined : localStorage
}

export function getToken(): AuthToken | null {
  const raw = storage()?.getItem(STORAGE_KEY)
  if (!raw) return null
  try {
    return JSON.parse(raw) as AuthToken
  } catch {
    return null
  }
}

export function setToken(token: AuthToken): void {
  storage()?.setItem(STORAGE_KEY, JSON.stringify(token))
}

export function clearToken(): void {
  storage()?.removeItem(STORAGE_KEY)
}

/** Decodes a JWT's payload without verifying its signature (verification is the backend's job). */
export function decodeJwt(accessToken: string): JwtClaims | null {
  const payload = accessToken.split('.')[1]
  if (!payload) return null
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/')
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    )
    return JSON.parse(json) as JwtClaims
  } catch {
    return null
  }
}

export function isTokenValid(token: AuthToken | null): token is AuthToken {
  if (!token) return false
  const claims = decodeJwt(token.access_token)
  return claims != null && claims.exp * 1000 > Date.now()
}

/** Whether `token`'s scopes overlap `requiredScopes` — a valid token alone isn't enough, the app's own scope gate (`createAuthProvider({ requiredScopes })`) also has to pass. */
export function hasRequiredScope(token: AuthToken, requiredScopes: string[]): boolean {
  const claims = decodeJwt(token.access_token)
  return claims != null && claims.scopes.some((scope) => requiredScopes.includes(scope))
}
